import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../api/client';
import { getWorkspaceId } from '../api/workspaces';

export interface ModuleInfo {
  moduleId: string;
  displayName: string;
  description?: string | null;
  dependencies: string[];
  maturity: string;
  enabled: boolean;
  /** ADR-008: the shop entitlement feed granted this module (prenumerata badge). */
  subscribed: boolean;
}

/** Mirrors ModulesController.WorkspaceModulesDto (ADR-007). */
interface WorkspaceModulesDto {
  workspaceId: string | null;
  effective: ModuleInfo[];
  catalog: ModuleInfo[];
}

interface ModulesContextType {
  /** The active workspace's effective module set (route/nav gating source). */
  modules: ModuleInfo[];
  /** The full catalog with per-module enabled flags (admin toggles). */
  catalog: ModuleInfo[];
  isModuleActive: (moduleId: string) => boolean;
  /** Re-read the workspace's module set (call after switching workspaces or
   * toggling modules in admin). */
  reload: () => Promise<void>;
  loading: boolean;
}

const ModulesContext = createContext<ModulesContextType>({
  modules: [],
  catalog: [],
  isModuleActive: () => true, // Default: all modules active (graceful degradation)
  reload: async () => {},
  loading: true,
});

export function ModuleProvider({ children }: { children: ReactNode }) {
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [catalog, setCatalog] = useState<ModuleInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const data = await api.get<WorkspaceModulesDto>('/modules');
      setModules(data.effective ?? []);
      setCatalog(data.catalog ?? []);
    } catch {
      // If module API fails, treat as all modules active (graceful degradation)
      // This ensures the app works even if the module endpoint is unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    reload().finally(() => { if (cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload, getWorkspaceId()]); // re-read when the active workspace changes

  const isModuleActive = (moduleId: string): boolean => {
    // During loading or if API failed, allow all modules (graceful degradation)
    if (loading) return true;
    if (modules.length === 0) return true;
    return modules.some(m => m.moduleId === moduleId);
  };

  return (
    <ModulesContext.Provider value={{ modules, catalog, isModuleActive, reload, loading }}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  return useContext(ModulesContext);
}
